import { useParams } from "react-router-dom";
import { useEffect } from "react";

import { ChatContainer } from "@/components/Chat/ChatContainer";
import { useArtefactStore } from "@/stores/ArtefactStore";
import { useChatStore } from "@/stores/ChatStore";
import { useWorkflowStore } from "@/stores/WorkflowStore";
import { logger } from "@/telemetry";

export const WorkflowPage: React.FC = () => {
  let { workflowId } = useParams();
  const clearAllArtefactState = useArtefactStore((state) => state.clearAllState);
  const newChat = useChatStore((state) => state.newChat);
  const setWorkflow = useWorkflowStore((state) => state.setWorkflow);

  if (!workflowId) {
    workflowId = "chat_agent"; // use default workflow id
  }

  // Force complete state reset when mounting workflow page
  // This ensures a fresh start regardless of environment caching
  useEffect(() => {
    logger.info("🔄 WorkflowPage: Forcing complete state reset on mount for workflow", {
      workflowId: String(workflowId),
    });

    // Clear all chat state and SSE connections
    newChat();

    // Clear all artefact state and localStorage persistence
    clearAllArtefactState();

    // Clear any workflow-specific localStorage keys that might persist
    try {
      const keysToRemove = [];

      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);

        if (
          key &&
          (key.startsWith("chat_") || key.startsWith("workflow_") || key.includes("artefact"))
        ) {
          keysToRemove.push(key);
        }
      }
      keysToRemove.forEach((key) => localStorage.removeItem(key));

      if (keysToRemove.length > 0) {
        logger.info("🧹 WorkflowPage: Cleared workflow-related localStorage keys", {
          keys: keysToRemove.join(", "),
        });
      }
    } catch (error) {
      logger.warn("Failed to clear workflow-related localStorage", { error: String(error) });
    }

    return () => {
      // Clear WorkflowStore so stale data doesn't leak to other pages
      setWorkflow(null);
    };
  }, [workflowId, newChat, clearAllArtefactState, setWorkflow]);

  return (
    <section className="max-h-screen">
      {workflowId && <ChatContainer workflowId={workflowId} />}
    </section>
  );
};
